import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { primaryDebtor, secondaryFilingDebtor } from '../../utils/debtors';

// Alias type dropdown options
const ALIAS_TYPE_CHOICES = [
  { value: 'fka', label: 'FKA' },
  { value: 'aka', label: 'AKA' },
  { value: 'dba', label: 'DBA' },
  { value: 'fdba', label: 'FDBA' },
  { value: 'pdba', label: 'PDBA' },
  { value: 'pka', label: 'PKA' },
  { value: 'ta', label: 'TA' },
];

// Simple street parser: house number, street name, street type
function parseStreet(street = '') {
  const parts = street.trim().split(/\s+/);
  if (parts.length < 2) {
    return { houseNumber: '', streetName: street, streetType: '' };
  }
  const houseNumber = parts[0];
  const streetType = parts[parts.length - 1];
  const streetName = parts.slice(1, parts.length - 1).join(' ');
  return { houseNumber, streetName, streetType };
}
const API_URL = process.env.REACT_APP_API_URL;
export default function ClientOneView() {
  const { caseId } = useParams();
  const { retainerStatus } = useOutletContext();
  const [loading, setLoading] = useState(true);

  const emptyPerson = {
    id: null,
    firstName: '',
    middleName: '',
    lastName: '',
    previousNames: [], // array of { first, middle, last, aliasType }
    ssn: '',
    ssnConfirm: '',
    address: { houseNumber: '', streetName: '', streetType: '', city: '', state: '', zip: '' },
  };

  const [primary, setPrimary] = useState({ ...emptyPerson });
  const [joint, setJoint] = useState(null);
  const [reports, setReports] = useState([]);       // existing pulled reports
  const [reportsLoading, setReportsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/case/${localStorage.getItem('clientCaseId')}/client-info-oneview/`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${localStorage.getItem("clientToken")}`,
      },
    })
      .then(res => res.json())
      .then(data => {
        // 'data' is now an array of debtor objects
        // Always show the primary debtor first
        const primaryData = primaryDebtor(data, { filingOnly: true }) || {};
        // Only include the joint debtor if they exist and are also filing
        const jointDataRaw = secondaryFilingDebtor(data);
        const jointData = jointDataRaw ? jointDataRaw : null;

        // Helper to map API object to form state
        const mapToPerson = apiObj => {
          const addr = parseStreet(apiObj.address1 || '');
          const initialSsn = apiObj.ssn || '';
          return {
            id: apiObj.id || null,
            firstName: apiObj.first_name || '',
            middleName: apiObj.middle_name || '',
            lastName: apiObj.last_name || '',
            previousNames: apiObj.aliases || [],
            ssn: initialSsn,
            ssnConfirm: initialSsn,
            address: {
              ...addr,
              city: apiObj.city || '',
              state: apiObj.state || '',
              zip: apiObj.postal_code || '',
            },
          };
        };

        // Populate primary form
        setPrimary(mapToPerson(primaryData));
        // Populate joint if present
        if (jointData) {
          setJoint(mapToPerson(jointData));
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [caseId]);

  // Fetch existing credit reports for this case
  useEffect(() => {
    setReportsLoading(true);
    fetch(`${API_URL}/case/${localStorage.getItem('clientCaseId')}/oneview-download/`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${localStorage.getItem("clientToken")}`,
      },
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch existing reports');
        return res.json();
      })
      .then(data => {
        // Assume `data` is an array of report objects with fields { role, report_id, filename }
        setReports(data);
      })
      .catch(() => {
        setReports([]);
      })
      .finally(() => {
        setReportsLoading(false);
      });
  }, [caseId]);

  // Handlers
  const handlePersonChange = (personKey, field) => e => {
    const setter = personKey === 'primary' ? setPrimary : setJoint;
    const person = personKey === 'primary' ? primary : joint;
    setter({ ...person, [field]: e.target.value });
  };
  const handleAddressChange = (personKey, field) => e => {
    const setter = personKey === 'primary' ? setPrimary : setJoint;
    const person = personKey === 'primary' ? primary : joint;
    setter({
      ...person,
      address: { ...person.address, [field]: e.target.value },
    });
  };
  const handleAliasChange = (personKey, idx, field) => e => {
    const setter = personKey === 'primary' ? setPrimary : setJoint;
    const person = personKey === 'primary' ? primary : joint;
    const aliases = [...person.previousNames];
    aliases[idx] = { ...aliases[idx], [field]: e.target.value };
    setter({ ...person, previousNames: aliases });
  };
  const addAlias = personKey => {
    const setter = personKey === 'primary' ? setPrimary : setJoint;
    const person = personKey === 'primary' ? primary : joint;
    setter({
      ...person,
      previousNames: [...person.previousNames, { first: '', middle: '', last: '', aliasType: 'aka' }],
    });
  };
  const removeAlias = (personKey, idx) => {
    const setter = personKey === 'primary' ? setPrimary : setJoint;
    const person = personKey === 'primary' ? primary : joint;
    const aliases = person.previousNames.filter((_, i) => i !== idx);
    setter({ ...person, previousNames: aliases });
  };

  // Format a string of digits as XXX-XX-XXXX
  function formatSSN(value) {
    const digits = value.replace(/\D/g, '').slice(0, 9);
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0,3)}-${digits.slice(3)}`;
    return `${digits.slice(0,3)}-${digits.slice(3,5)}-${digits.slice(5)}`;
  }

  // Handler to apply SSN formatting and update state
  const handleSSNChange = (personKey) => (e) => {
    const setter = personKey === 'primary' ? setPrimary : setJoint;
    const person = personKey === 'primary' ? primary : joint;
    const formatted = formatSSN(e.target.value);
    setter({ ...person, ssn: formatted });
  };

  // Handler to update SSN confirmation field
  const handleSSNConfirmChange = (personKey) => (e) => {
    const setter = personKey === 'primary' ? setPrimary : setJoint;
    const person = personKey === 'primary' ? primary : joint;
    const formatted = formatSSN(e.target.value);
    setter({ ...person, ssnConfirm: formatted });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    // Validate SSN fields match and correct format
    const ssnPattern = /^\d{3}-\d{2}-\d{4}$/;
    if (!ssnPattern.test(primary.ssn) || primary.ssn !== primary.ssnConfirm) {
      alert('Primary SSN entries must match and be in XXX-XX-XXXX format');
      return;
    }
    if (joint) {
      if (!ssnPattern.test(joint.ssn) || joint.ssn !== joint.ssnConfirm) {
        alert('Joint SSN entries must match and be in XXX-XX-XXXX format');
        return;
      }
    }
    const payload = { primary, joint };
    // Show submitting overlay
    setIsSubmitting(true);
    try {
      // Update debtor information
      const updateRes = await fetch(`${API_URL}/case/${localStorage.getItem('clientCaseId')}/client-info-oneview/`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem("clientToken")}`,
          },
        body: JSON.stringify(payload),
      });
      if (!updateRes.ok) {
        throw new Error('Failed to update debtor information');
      }
      // Pull credit reports after debtor update
      const reportRes = await fetch(`${API_URL}/case/${localStorage.getItem('clientCaseId')}/oneview/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem("clientToken")}`,
          },
        body: JSON.stringify(payload),
      });
      if (!reportRes.ok) {
        throw new Error('Failed to pull credit reports');
      }
      await reportRes.json();
      window.location.reload();
    } catch (err) {
      alert(err.message);
    } finally {
      // Keep the overlay visible while credit reports are processing; do not set false here
      // The overlay will stay until the component reloads or unmounts
    }
  };
  if (isSubmitting) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-80 z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl p-6 text-center max-w-sm">
          <div className="flex flex-col items-center">
            {/* Spinner */}
            <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mb-4"></div>
            <h2 className="text-lg font-semibold mb-2">Processing Credit Reports</h2>
            <p className="text-gray-700">
              Please wait up to 1 minute for your credit reports to process. Do not refresh your browser.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <p className="p-4">Loading...</p>;
  // Wait until retainerStatus is provided before checking signatures
  if (typeof retainerStatus === 'undefined' || (Array.isArray(retainerStatus) && retainerStatus.length === 0)) {
    return <p className="p-4">Loading status...</p>;
  }
  // If we are still loading report info, show loading indicator
  if (reportsLoading) return <p className="p-4">Checking for existing reports...</p>;
  // If any reports already exist, show overlay instead of form
  const reportThreshold = joint ? 2 : 1;
  if (reports && reports.length >= reportThreshold) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-80 z-50 p-4">
        <div className="bg-white max-w-lg w-full rounded-lg shadow-xl overflow-hidden p-6">
          <h2 className="text-xl font-semibold mb-4">Credit Report Already Pulled</h2>
          <p className="mb-4 text-gray-700">
          You have already pulled your credit report{joint ? "s" : ""}. Below are the download links:
          </p>
          <ul className="list-disc list-inside mb-6">
            {reports.map((r, idx) => (
              <li key={idx} className="mb-2">
                <a
                  href={`${r.upload_url}`}
                  className="text-indigo-600 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download {r.filename} Report
                </a>
              </li>
            ))}
          </ul>
          <p className="mb-4 text-gray-700">
            Please review your creditors and add any additional ones on the creditors page.
          </p>
          <a
            href={`/client/dashboard/creditors`}
            className="inline-block px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Go to Creditors
          </a>
        </div>
      </div>
    );
  }

  const statuses = Array.isArray(retainerStatus) ? retainerStatus : [retainerStatus];
  const pending = statuses.filter(item => !item.signed);

  if (pending.length > 0) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-80 z-50 p-4">
        <div className="bg-white max-w-md w-full rounded-lg shadow-xl overflow-hidden">
          <div className="bg-indigo-600 px-6 py-4">
            <h2 className="text-white text-xl font-semibold">Retainer Agreement Pending</h2>
          </div>
          <div className="p-6">
            <p className="mb-4 text-gray-700">
              The following person{pending.length > 1 ? 's have' : ' has'} not yet signed the retainer agreement:
            </p>
            <ul className="list-disc list-inside mb-4 text-gray-800">
              {pending.map(person => (
                <li key={person.debtor_id}>{person.debtor_full_name}</li>
              ))}
            </ul>
            <p className="mb-6 text-gray-700">
              Please complete the retainer agreement before pulling your credit report.
            </p>
            <a
              href={`/client/dashboard/retainer/`}
              className="block text-center px-4 py-2 bg-indigo-600 text-white font-medium rounded hover:bg-indigo-700"
            >
              Finish Retainer
            </a>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-3xl mx-auto p-1">
      <h2 className="text-xl font-bold mb-2">Pull your Credit Report</h2>
      <form onSubmit={handleSubmit} className="space-y-8">
        {['primary', 'joint'].map(key => {
          const person = key === 'primary' ? primary : joint;
          if (key === 'joint' && !joint) return null;
          return (
            <fieldset key={key} className="border rounded p-1">
              <legend className="font-semibold text-lg mb-2">
                {key === 'primary' ? 'Debtor' : 'Joint Debtor'}
              </legend>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-1">
                <div>
                  <label className="block text-sm font-medium">First Name</label>
                  <input
                    type="text"
                    value={person.firstName}
                    onChange={handlePersonChange(key, 'firstName')}
                    className="mt-1 block w-full border rounded p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Middle Name</label>
                  <input
                    type="text"
                    value={person.middleName}
                    onChange={handlePersonChange(key, 'middleName')}
                    className="mt-1 block w-full border rounded p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Last Name</label>
                  <input
                    type="text"
                    value={person.lastName}
                    onChange={handlePersonChange(key, 'lastName')}
                    className="mt-1 block w-full border rounded p-2"
                  />
                </div>
              </div>

              <div className="mt-4">
                <h4 className="font-medium">Previous Names (last 8 years)</h4>
                {person.previousNames.map((alias, idx) => (
                  <div key={idx} className="grid grid-cols-1 sm:grid-cols-4 gap-0 mt-2 items-end">
                    <div>
                      <label className="block text-xs">First</label>
                      <input
                        type="text"
                        value={alias.first_name}
                        onChange={handleAliasChange(key, idx, 'first')}
                        className="mt-1 block w-full border rounded p-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs">Middle</label>
                      <input
                        type="text"
                        value={alias.middle_name}
                        onChange={handleAliasChange(key, idx, 'middle')}
                        className="mt-1 block w-full border rounded p-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs">Last</label>
                      <input
                        type="text"
                        value={alias.last_name}
                        onChange={handleAliasChange(key, idx, 'last')}
                        className="mt-1 block w-full border rounded p-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs">Type</label>
                      <select
                        value={alias.aliasType}
                        onChange={handleAliasChange(key, idx, 'aliasType')}
                        className="mt-1 block w-full border rounded p-1 text-sm"
                      >
                        {ALIAS_TYPE_CHOICES.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAlias(key, idx)}
                      className="text-red-600 text-sm mt-1"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addAlias(key)}
                  className="mt-2 inline-block text-indigo-600 text-sm underline"
                >
                  + Add previous name
                </button>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium">SSN</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={11}
                    value={person.ssn}
                    onChange={handleSSNChange(key)}
                    placeholder="XXX-XX-XXXX"
                    className="mt-1 block w-full border rounded p-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Confirm SSN</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={11}
                    value={person.ssnConfirm}
                    onChange={handleSSNConfirmChange(key)}
                    placeholder="XXX-XX-XXXX"
                    className="mt-1 block w-full border rounded p-2"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium">Address</label>
                  <div className="grid grid-cols-8 gap-2">
                    <input
                      type="text"
                      value={person.address.houseNumber}
                      onChange={handleAddressChange(key, 'houseNumber')}
                      placeholder="House #"
                      className="border rounded p-1 text-sm col-span-2"
                    />
                    <input
                      type="text"
                      value={person.address.streetName}
                      onChange={handleAddressChange(key, 'streetName')}
                      placeholder="Street Name"
                      className="border rounded p-1 text-sm col-span-4"
                    />
                    <input
                      type="text"
                      value={person.address.streetType}
                      onChange={handleAddressChange(key, 'streetType')}
                      placeholder="Type (Rd, St, etc)"
                      className="border rounded p-1 text-sm col-span-2"
                    />
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    <input
                      type="text"
                      value={person.address.city}
                      onChange={handleAddressChange(key, 'city')}
                      placeholder="City"
                      className="border rounded p-2 text-sm col-span-2"
                    />
                    <input
                      type="text"
                      value={person.address.state}
                      onChange={handleAddressChange(key, 'state')}
                      placeholder="State"
                      className="border rounded p-2 text-sm col-span-1"
                    />
                    <input
                      type="text"
                      value={person.address.zip}
                      onChange={handleAddressChange(key, 'zip')}
                      placeholder="ZIP"
                      className="border rounded p-2 text-sm col-span-1"
                    />
                  </div>
                </div>
              </div>
            </fieldset>
          );
        })}

        <div className="pt-4">
          <button
            type="submit"
            className="px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Submit Information
          </button>
        </div>
      </form>
    </div>
  );
}
